import { Navigate, Outlet } from "react-router-dom";
import { RoutePath } from "@/routes";
import { authService, echoService } from "@/services";
import {
  subscribeToLandscapeUpdates,
  unsubscribeFromLandscapeUpdates,
  subscribeToKeywordBankEnrichment,
  unsubscribeFromKeywordBankEnrichment,
  subscribeToLandscapeProductsUpdates,
  unsubscribeFromLandscapeProductsUpdates,
  subscribeToScorecardUpdates,
  unsubscribeFromScorecardUpdates,
  subscribeToListingUpdates,
  unsubscribeFromListingUpdates,
} from "@/services/echo.service";
import type { ScorecardStatusEvent, ListingStatusEvent } from "@/services/echo.service";
import { accountApi } from "@/resources/account";
import { handleListingBroadcast } from "@/resources/listing/listing.events";
import { clearAuditInitiated } from "@/resources/listing/listing.initiator";
import { handleLandscapeBroadcast, landscapeProductsUpdatedInvalidation } from "@/resources/landscape/landscape.events";
import { useKeepSessionAlive } from "@/hooks/useKeepSessionAlive";
import { useKpsRecalculationToast } from "@/components/kps-recalculation/useKpsRecalculationToast";
import FullScreenLoader from "./ui/full-screen-loader";
import { AppDataProvider } from "@/contexts/AppDataContext";
import { ViewerReadOnlyProvider } from "@/contexts/ViewerReadOnlyContext";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import queryClient from "@/query-client";
import { ReconnectingBanner } from "./shared/ReconnectingBanner";
import type { GetLandscapesResponse, ScorecardResponse } from "@/types";
import { AppLayout } from "./layout";

function AuthenticatedApp() {
  const { data: account, isLoading, isError } = accountApi.useGetAccount();
  const [echoDisconnected, setEchoDisconnected] = useState(false);

  useKeepSessionAlive();

  // FR-8 to FR-12 (`components/keyword.md § The toast`): polled and rendered here, not on the
  // Settings screen, because this is the one component that wraps every authenticated screen.
  // Internally gated to `owner`/`admin` -- a `viewer`/`editor` account issues no request.
  useKpsRecalculationToast(account);

  useEffect(() => {
    if (!account?.id || !account?.organization_id) return;

    echoService.connect(account.id, account.organization_id);

    echoService.subscribeToNotifications(
      account.id,
      account.organization_id,
      (notification) => {
        const priority = notification.priority ?? "info";
        if (priority === "high") {
          toast.error(notification.title, { description: notification.body });
        } else if (priority === "low") {
          toast.info(notification.title, { description: notification.body });
        } else {
          toast.success(notification.title, { description: notification.body });
        }
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
        queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
      }
    );

    subscribeToKeywordBankEnrichment(account.organization_id, () => {
      queryClient.invalidateQueries({ queryKey: ['hero', 'product', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['product', 'keywords'] });
      queryClient.invalidateQueries({ queryKey: ['product', 'detail'] });
    });

    subscribeToLandscapeProductsUpdates(account.organization_id, (event) => {
      queryClient.invalidateQueries(landscapeProductsUpdatedInvalidation(event));
    });

    subscribeToLandscapeUpdates(account.organization_id, (event) => {
      handleLandscapeBroadcast(event, {
        getCachedStatus: (landscapeId) => {
          for (const [, data] of queryClient.getQueriesData<GetLandscapesResponse>({ queryKey: ['landscapes'] })) {
            for (const group of data?.landscapes ?? []) {
              const instance = group.instances?.find((candidate) => candidate.id === landscapeId);
              if (instance) return instance.status;
            }
          }
          return undefined;
        },
        invalidate: () => queryClient.invalidateQueries({ queryKey: ['landscapes'] }),
        patchStatusInfo: (landscapeId, statusInfo) => {
          queryClient.setQueriesData<GetLandscapesResponse>(
            { queryKey: ['landscapes'] },
            (oldData) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                landscapes: oldData.landscapes?.map((group) => ({
                  ...group,
                  instances: group.instances?.map((instance) =>
                    instance.id === landscapeId
                      ? { ...instance, status_info: statusInfo }
                      : instance
                  ),
                })),
              };
            }
          );
        },
      });
    });

    subscribeToScorecardUpdates(account.organization_id, (event: ScorecardStatusEvent) => {
      if (event.status === 'completed' || event.status === 'failed') {
        const storageKey = `scorecard:generating:${event.asin}`;
        localStorage.removeItem(storageKey);
        clearAuditInitiated(event.asin);
        queryClient.invalidateQueries({ queryKey: ["scorecards", "show", event.scorecard_id] });
        queryClient.invalidateQueries({ queryKey: ["scorecards", "list", event.asin] });
      } else if (event.status === 'processing') {
        queryClient.setQueryData<ScorecardResponse>(
          ["scorecards", "show", event.scorecard_id],
          (existing) => {
            if (existing) {
              return { ...existing, status: 'processing', step: event.step } as ScorecardResponse;
            }
            return { id: event.scorecard_id, asin: event.asin, status: 'processing', step: event.step } as unknown as ScorecardResponse;
          }
        );
      }
    });

    subscribeToListingUpdates(account.organization_id, (event: ListingStatusEvent) => {
      handleListingBroadcast(event, account.id, {
        invalidate: (queryKey) => queryClient.invalidateQueries({ queryKey }),
        toastSuccess: (title, description) => toast.success(title, { description }),
        toastError: (message) => toast.error(message),
      });
    });

    const echo = echoService.getEcho();
    const pusherConnector = (
      echo as unknown as {
        connector: {
          pusher: {
            connection: {
              bind: (event: string, cb: () => void) => void;
              unbind: (event: string, cb: () => void) => void;
            };
          };
        };
      }
    ).connector.pusher;

    const onDisconnected = () => setEchoDisconnected(true);
    const onConnected = () => setEchoDisconnected(false);

    pusherConnector.connection.bind("disconnected", onDisconnected);
    pusherConnector.connection.bind("connected", onConnected);
    pusherConnector.connection.bind("unavailable", onDisconnected);

    return () => {
      pusherConnector.connection.unbind("disconnected", onDisconnected);
      pusherConnector.connection.unbind("connected", onConnected);
      pusherConnector.connection.unbind("unavailable", onDisconnected);
      unsubscribeFromLandscapeUpdates(account.organization_id);
      unsubscribeFromKeywordBankEnrichment(account.organization_id);
      unsubscribeFromLandscapeProductsUpdates(account.organization_id);
      unsubscribeFromScorecardUpdates(account.organization_id);
      unsubscribeFromListingUpdates(account.organization_id);
    };
  }, [account?.id, account?.organization_id]);

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!account || isError) {
    return <Navigate to={RoutePath.Login} replace />;
  }

  return (
    <AppDataProvider>
      <ViewerReadOnlyProvider>
        <AppLayout>
          <ReconnectingBanner visible={echoDisconnected} />
          <Outlet />
        </AppLayout>
      </ViewerReadOnlyProvider>
    </AppDataProvider>
  );
}

export default function ProtectedRoute() {
  if (!authService.isAuthenticated()) {
    return <Navigate to={RoutePath.Login} replace />;
  }

  return <AuthenticatedApp />;
}
